
import { useRef, useEffect, useCallback } from 'react';

/**
 * useTimerWorker
 * Manages the Web Worker for the timer and handles tick/completion events.
 * 
 * @param {Function} onTick - Callback for updating remaining time (local state).
 * @param {Function} onComplete - Callback for when the timer reaches zero.
 * @returns {object} Actions to control the worker.
 */
export const useTimerWorker = (onTick, onComplete) => {
  const workerRef = useRef(null);
  const onTickRef = useRef(onTick);
  const onCompleteRef = useRef(onComplete);

  // Keep refs up to date to avoid stale closures in worker callback
  useEffect(() => {
    onTickRef.current = onTick;
    onCompleteRef.current = onComplete;
  }, [onTick, onComplete]);

  useEffect(() => {
    const workerUrl = `${process.env.PUBLIC_URL}/workers/timerWorker.js`;
    const worker = new Worker(workerUrl);
    workerRef.current = worker;

    worker.onmessage = (e) => {
      const { type, remainingTime } = e.data;

      if (type === 'tick') {
        if (onTickRef.current) onTickRef.current(remainingTime);
      } else if (type === 'completed') {
        if (onCompleteRef.current) onCompleteRef.current({ isManual: true });
      }
    };

    return () => {
      worker.terminate();
    };
  }, []); // Empty dependency array to ensure worker is created only once

  const startWorker = useCallback((duration) => {
    if (workerRef.current) {
      workerRef.current.postMessage({ command: 'start', duration });
    }
  }, []);

  const stopWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ command: 'stop' });
    }
  }, []);

  const resetWorker = useCallback((duration) => {
    if (workerRef.current) {
      workerRef.current.postMessage({ command: 'reset', duration });
    }
  }, []);

  return {
    startWorker,
    stopWorker,
    resetWorker,
    workerRef, // Exposed if absolutely needed, but prefer actions
  };
};
